import React, { useCallback, useEffect, useState } from "react";
import {
  fetchAgentWriteProposals,
  resolveAgentWriteProposal,
} from "../services/api";
import { userCanContribute } from "../utils/userRoles";

/**
 * Editor+ queue for agent propose-mode writes (autonomous / MCP).
 */
const AgentWriteProposalsPanel = ({ hideHeader = false }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const canReview = userCanContribute();

  const load = useCallback(async () => {
    if (!canReview) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await fetchAgentWriteProposals({ status: "pending" });
      setItems(Array.isArray(data?.proposals) ? data.proposals : []);
    } catch (e) {
      setError(e.message || "Failed to load proposals");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [canReview]);

  useEffect(() => {
    load();
  }, [load]);

  const decide = async (id, decision) => {
    setBusyId(id);
    setError("");
    try {
      await resolveAgentWriteProposal(id, decision);
      await load();
    } catch (e) {
      setError(e.message || "Failed to resolve proposal");
    } finally {
      setBusyId("");
    }
  };

  if (!canReview) {
    return (
      <p className="text-sm text-slate-600 p-4">
        Editor or Administrator role is required to review agent write proposals.
      </p>
    );
  }

  return (
    <div className="p-4 space-y-3">
      {!hideHeader ? (
        <div>
          <h3 className="text-lg font-semibold text-slate-900">
            Agent write proposals
          </h3>
          <p className="text-sm text-slate-600 mt-1">
            Autonomous agent runs queue catalog changes here. Accept applies the
            write; reject discards it.
          </p>
        </div>
      ) : (
        <p className="text-sm text-slate-600">
          Autonomous agent runs queue catalog changes here. Accept applies the
          write; reject discards it.
        </p>
      )}

      {error ? (
        <p className="text-sm text-red-600" role="alert">
          {error}
        </p>
      ) : null}

      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={load}
          className="text-sm text-slate-700 underline"
          disabled={loading}
        >
          Refresh
        </button>
        <span className="text-xs text-slate-500">
          {loading ? "Loading…" : `${items.length} pending`}
        </span>
      </div>

      {!loading && items.length === 0 ? (
        <p className="text-sm text-slate-500">No pending agent write proposals.</p>
      ) : null}

      <ul className="space-y-3">
        {items.map((p) => (
          <li
            key={p.id}
            className="border border-slate-200 rounded-md p-3 text-sm space-y-2"
          >
            <div className="flex flex-wrap gap-x-3 gap-y-1 text-slate-800">
              <span className="font-medium">{p.action_type || "write"}</span>
              <span className="text-slate-500">{p.tool_name}</span>
              {p.created_at ? (
                <span className="text-slate-400">{p.created_at}</span>
              ) : null}
            </div>
            <pre className="text-xs bg-slate-50 p-2 overflow-x-auto max-h-40 rounded">
              {JSON.stringify(p.payload || {}, null, 2)}
            </pre>
            <div className="flex gap-2">
              <button
                type="button"
                disabled={busyId === p.id}
                onClick={() => decide(p.id, "accepted")}
                className="px-3 py-1.5 text-sm bg-emerald-700 text-white rounded"
              >
                Accept
              </button>
              <button
                type="button"
                disabled={busyId === p.id}
                onClick={() => decide(p.id, "rejected")}
                className="px-3 py-1.5 text-sm bg-slate-200 text-slate-800 rounded"
              >
                Reject
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default AgentWriteProposalsPanel;
